import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { auth, db as fdb } from './firebase';
import { db } from './db';
import { listDocuments, listAllDocuments, setCurrentOwnerId } from './library';
import { stopSync } from './sync';
import { zipStore, type ZipEntry } from './zip';
import { download, slugify } from './export/download';
import { t } from '../i18n';

/** Download every (non-trashed) document as a `.zip` of `.md` files. */
export async function exportAllDocuments(): Promise<number> {
  const docs = await listDocuments();
  const enc = new TextEncoder();
  const used = new Map<string, number>();
  const entries: ZipEntry[] = docs.map((d) => {
    const base = slugify(d.title || 'untitled') || 'untitled';
    const n = (used.get(base) ?? 0) + 1;
    used.set(base, n);
    const name = n > 1 ? `${base}-${n}.md` : `${base}.md`;
    return { name, data: enc.encode(d.content) };
  });
  const stamp = new Date().toISOString().slice(0, 10);
  download(`markbird-export-${stamp}.zip`, zipStore(entries));
  return entries.length;
}

/**
 * Delete the signed-in account and its synced (cloud) documents. Local
 * documents are kept but un-owned, so they remain available offline as
 * local-only. May throw `auth/requires-recent-login` if the session is stale —
 * the caller should ask the user to sign in again and retry.
 */
export async function deleteAccountAndCloudData(): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in.');
  const uid = user.uid;

  // Delete the shares this account created first, while there is still an ID
  // token to prove who created them. Share snapshots are stored with no owner,
  // so the documents query below never reached them; once the user is deleted,
  // nothing could. If this fails, stop: an account deleted with its shares left
  // behind is exactly the outcome the user asked us to prevent, and it could not
  // be put right afterwards.
  const token = await user.getIdToken();
  const res = await fetch('/api/account/shares', {
    method: 'DELETE',
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(t('account.sharesDeleteFail'));

  // Stop mirroring so teardown writes don't bounce back through sync.
  stopSync();

  // Remove this owner's Firestore documents.
  const snap = await getDocs(query(collection(fdb, 'documents'), where('ownerId', '==', uid)));
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref).catch(() => {})));

  // Keep local copies but detach ownership (become local-only).
  const all = await listAllDocuments();
  for (const d of all) {
    if (d.ownerId === uid) await db.documents.update(d.id, { ownerId: null });
  }
  setCurrentOwnerId(null);

  // Finally delete the auth user itself.
  await deleteUser(user);
}
